import {
  type InventorizationRegisterReading,
  readInventorizationRegisters,
} from '@/features/inventorization/domain/inventorization-reading';
import {
  createInventorizationRegisterLabel,
  DEFAULT_INVENTORIZATION_REGISTER_ID,
} from '@/features/inventorization/domain/inventorization-registers';
import { readInventorizeRegisterReviewFieldId } from '@/features/inventorization/domain/review/inventorize-review-field-ids';
import type { InventorizeReviewField } from '@/features/inventorization/domain/review/inventorize-review-fields';
import { INVENTORIZE_REVIEW_STATUSES } from '@/features/inventorization/domain/review/inventorize-review-fields';
import { reviewText } from '@/features/inventorization/review-i18n';
import { type CaptureSession, type LocalPhotoRef, PHOTO_CONTEXTS } from '@/shared/capture/capture-session';

export function createRegisterReviewGroups(session: CaptureSession, fields: InventorizeReviewField[]) {
  const registers = readInventorizationRegisters(session);
  const registerIds = new Set(registers.map(register => register.id));
  const fieldRegisterIds = fields.map(field => readRegisterIdFromFieldId(field.id)).filter((id): id is string => !!id);
  const extraRegisters = fieldRegisterIds
    .filter(id => !registerIds.has(id))
    .map(
      (id, index): InventorizationRegisterReading => ({
        counter: '',
        id,
        label: createInventorizationRegisterLabel(registers.length + index),
        obis: '',
        photoRef: id,
        tariff: '',
        unit: '',
      }),
    );

  return [...registers, ...extraRegisters];
}

export function getRegisterFields(fields: InventorizeReviewField[], registerId: string) {
  return fields.filter(field => readInventorizeRegisterReviewFieldId(field.id)?.registerId === registerId);
}

export function getRegisterPhotos(
  photos: LocalPhotoRef[],
  register: Pick<InventorizationRegisterReading, 'id' | 'photoRef'>,
) {
  return photos.filter(
    photo =>
      photo.context === PHOTO_CONTEXTS.register &&
      (photo.registerId === register.id ||
        photo.registerId === register.photoRef ||
        (!photo.registerId && register.id === DEFAULT_INVENTORIZATION_REGISTER_ID)),
  );
}

export function formatRegisterFieldLabel(field: InventorizeReviewField) {
  if (field.id.endsWith('.label')) {
    return reviewText('register.field.label', 'Label');
  }

  if (field.id.endsWith('.counter')) {
    return reviewText('register.field.counter', 'Counter');
  }

  if (field.id.endsWith('.unit')) {
    return reviewText('register.field.unit', 'Unit');
  }

  if (field.id.endsWith('.obis')) {
    return 'OBIS';
  }

  if (field.id.endsWith('.tariff')) {
    return reviewText('register.field.tariff', 'Tariff');
  }

  return field.label;
}

export function isFilledStatus(status: InventorizeReviewField['status']) {
  return (
    status === INVENTORIZE_REVIEW_STATUSES.manual ||
    status === INVENTORIZE_REVIEW_STATUSES.parsed ||
    status === INVENTORIZE_REVIEW_STATUSES.accepted
  );
}

function readRegisterIdFromFieldId(fieldId: string) {
  const match = /^registers\.([^.]+)\./.exec(fieldId);

  return match?.[1];
}
